import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { Customer, CustomerFormData } from '@/shared/types/customerTypes';
import { CreateCustomerUserData, Organization, UpdateCustomerUserData, User } from '@/shared/types/userTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

// Get Customers
export const useAccountCustomers = (enabled: boolean) => {
  return useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Customer>>(
        `/api/v1/customers`,
      );
      return response.data.results || [];
    },
    enabled: enabled,
  });
};

/** Infinite scroll fetch for customers load-more. */
export const useInfiniteAccountCustomers = () => {
  return useInfiniteQuery({
    queryKey: ['customers-infinite'],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<Customer>>(
        `/api/v1/customers`,
        { params: { page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
  });
};

// Create Customer Mutation
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerData: CustomerFormData) => {
      return apiClient.post<APIMessageResponse>(
        `/api/v1/customers`,
        {
          body: customerData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['created-customer'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Update Customer Mutation
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, customerData }: { customerId: string; customerData: CustomerFormData }) => {
      return apiClient.put<APIMessageResponse>(
        `/api/v1/customers/${customerId}`,
        {
          body: customerData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['updated-customer'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Delete Customer Mutation
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId }: { customerId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/api/v1/customers/${customerId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deleted-customer'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Use Account Users
export const useCustomerUsers = (customerId: string) => {
  return useQuery({
    queryKey: ['customer-users', customerId],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<User>>(
        `/api/v1/customers/${customerId}/users`,
      );
      return response.data.results;
    },
    enabled: !!customerId,
  });
};

// Create Customer User Mutation
export const useCreateCustomerUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, userData }: { customerId: string; userData: CreateCustomerUserData }) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/customers/${customerId}/users`,
        {
          body: userData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['created-customer-user'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

// Update Customer User Mutation
export const useUpdateCustomerUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, userId, userData }: { customerId: string; userId: string; userData: UpdateCustomerUserData }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/customers/${customerId}/users/${userId}`,
        {
          body: userData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-user-accounts-updated'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

export const useRemoveCustomerUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, userId }: { customerId: string; userId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/api/v1/customers/${customerId}/users/${userId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['removed-customer-user'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Use Account Users
export const useCustomerOrganizations = (customerId: string) => {
  return useQuery({
    queryKey: ['customer-organizations', customerId],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Organization>>(
        `/api/v1/customers/${customerId}/organizations`,
      );
      return response.data.results;
    },
    enabled: !!customerId,
  });
};